import { useParams, Link, Navigate } from "react-router-dom";
import { MonoLabel } from "@/site/components/Atoms";
import { getInsightBySlug, getAdjacent } from "@/site/pages/insightsContent";

function hostFromUrl(u) {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u;
  }
}

function Block({ block }) {
  const [type, value] = block;

  if (type === "p") {
    return (
      <p className="text-[15px] leading-[1.95] text-white/75">
        {value}
      </p>
    );
  }

  if (type === "quote") {
    return (
      <blockquote
        className="relative pl-6 py-1 my-2 border-l border-[#9DB4FF]/60 font-display text-[20px] leading-[1.35] text-white/90 italic"
      >
        {value}
      </blockquote>
    );
  }

  if (type === "ul") {
    return (
      <ul className="space-y-2.5 pl-0 list-none">
        {value.map((item, i) => (
          <li
            key={i}
            className="relative pl-6 text-[14.5px] leading-[1.75] text-white/75"
          >
            <span
              className="absolute left-0 top-[0.7em] w-2.5 h-px bg-[#9DB4FF]/70"
              aria-hidden="true"
            />
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (type === "refs") {
    return (
      <div className="my-2 border-l border-white/10 pl-5 py-2">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#9DB4FF]/80 font-mono mb-3">
          Reference links
        </div>
        <ul className="space-y-2">
          {value.map((url, i) => (
            <li key={i}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`insight-ref-${i}`}
                className="block text-[12.5px] leading-[1.55] text-white/55 hover:text-[#9DB4FF] transition-colors break-all"
              >
                <span className="text-white/40 mr-2">{hostFromUrl(url)}</span>
                <span className="text-white/30">{url.replace(/^https?:\/\//, "").replace(hostFromUrl(url), "")}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
}

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getInsightBySlug(slug);
  if (!article) return <Navigate to="/insights" replace />;
  const { prev, next } = getAdjacent(slug);

  return (
    <>
      {/* Hero */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-12 lg:pt-16">
        <Link
          to="/insights"
          data-testid="insight-back-link"
          className="inline-block text-[10px] tracking-[0.35em] uppercase text-white/55 hover:text-white transition-colors font-mono"
        >
          ← All insights
        </Link>

        <div className="mt-10 max-w-3xl">
          <div className="flex items-center gap-4">
            <MonoLabel>NOTE · {article.n}</MonoLabel>
            <span className="text-[10px] tracking-[0.35em] uppercase text-white/35 font-mono">
              Forealco Insights
            </span>
          </div>
          <h1
            data-testid="insight-title"
            className="h-display mt-6 text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.08]"
          >
            {article.title}
          </h1>
          <p className="mt-6 text-[15px] leading-[1.75] text-white/60 max-w-2xl">
            {article.dek}
          </p>
        </div>

        <div
          className="mt-12 h-px"
          style={{
            background:
              "linear-gradient(90deg, #9DB4FF 0%, rgba(157,180,255,0.15) 30%, transparent 70%)",
          }}
        />
      </section>

      {/* Body */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-14">
        <article
          data-testid="insight-article-body"
          className="max-w-[720px] space-y-6"
        >
          {article.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </article>
      </section>

      {/* Prev / Next */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-24">
        <div className="border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {prev ? (
            <Link
              to={`/insights/${prev.slug}`}
              data-testid="insight-prev"
              className="group block border border-white/10 hover:border-[#9DB4FF]/60 transition-colors p-6"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 font-mono">
                ← Previous · Note {prev.n}
              </div>
              <div className="mt-3 font-display text-[18px] leading-[1.3] text-white/90 group-hover:text-white">
                {prev.title}
              </div>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link
              to={`/insights/${next.slug}`}
              data-testid="insight-next"
              className="group block border border-white/10 hover:border-[#9DB4FF]/60 transition-colors p-6 md:text-right"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 font-mono">
                Next · Note {next.n} →
              </div>
              <div className="mt-3 font-display text-[18px] leading-[1.3] text-white/90 group-hover:text-white">
                {next.title}
              </div>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </section>
    </>
  );
}
